import React, { useRef, useState } from "react";
import { track } from "../lib/analytics";
import { PageHero } from "../components/site/kit";
import { Pager } from "../components/site/extras";
import { useRoles } from "../lib/careersApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED = [".pdf", ".doc", ".docx"];
const MAX = 5 * 1024 * 1024;
const field = "font-body w-full border border-[#161A15]/25 bg-white px-4 py-3.5 text-lg font-normal text-black outline-none transition-colors placeholder:text-black/45 focus:border-[#5f8f10]";

const BEFORE = [
  "What you are genuinely good at",
  "The kind of business problems you have worked on",
  "How you approach unfamiliar or ambiguous situations",
  "Whether you can connect thinking with action",
  "Where you believe your experience could strengthen SWITCH",
];

export default function Careers() {
  const roles = useRoles();
  const [form, setForm] = useState({ name: "", email: "", expertise: "", cover_letter: "" });
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) { setFile(null); return; }
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) { setError("Upload your CV as a PDF, DOC or DOCX file."); setFile(null); return; }
    if (f.size > MAX) { setError("The file must be smaller than 5 MB."); setFile(null); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.email.trim() || !form.email.includes("@")) return setError("Please enter a valid email address.");
    if (!form.expertise.trim()) return setError("Tell us briefly about your area of expertise.");
    if (!file) return setError("Upload your CV before submitting.");
    if (form.cover_letter.length > 1000) return setError("Keep the cover letter within 1,000 characters.");
    setError(""); setStatus("submitting"); track("career_application_submitted");
    const fd = new FormData();
    fd.append("name", form.name); fd.append("email", form.email); fd.append("expertise", form.expertise);
    fd.append("cover_letter", form.cover_letter); fd.append("cv", file);
    try {
      const res = await fetch(`${API}/careers`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("bad status");
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError("We could not submit your application. Your information has not been lost. Please try again.");
      track("career_application_error");
    }
  };

  return (
    <div className="font-body bg-white">
      <PageHero
        testId="careers-hero"
        eyebrow="Careers"
        crumbs={[{ label: "Home", href: "/" }, { label: "Careers" }]}
        title={<>Work on problems that do not fit neatly into one <span className="text-[#5f8f10]">discipline.</span></>}
        subcopy="SWITCH brings together people across strategy, brand, marketing, sales, technology, operations, finance and organisation. We look for people who think beyond their specialism, work closely with others, and move ideas into execution."
      />

      {roles.length > 0 && (
        <section data-testid="careers-roles" className="border-b border-black/10 bg-white">
          <div className="mx-auto max-w-[1360px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
            <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-black md:text-4xl">Open <span className="text-[#5f8f10]">roles.</span></h2>
            <div className="mt-10 border-t border-black/12">
              {roles.map((r) => (
                <div key={r.slug} data-testid={`role-${r.slug}`} className="grid gap-4 border-b border-black/12 py-8 md:grid-cols-12 md:gap-8">
                  <div className="md:col-span-4">
                    <h3 className="font-display text-2xl uppercase leading-tight tracking-tight text-black">{r.title}</h3>
                    <p className="font-body mt-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">{[r.location, r.type].filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="md:col-span-8">
                    {r.summary && <p className="font-body text-lg font-normal leading-relaxed text-black/85">{r.summary}</p>}
                    {r.description && <p className="font-body mt-3 text-base font-normal leading-relaxed text-black/65">{r.description}</p>}
                    <a href="#apply" className="font-body mt-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-black underline decoration-[#5f8f10] decoration-2 underline-offset-4">Apply below <span aria-hidden>↓</span></a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="apply" className="border-b border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto grid max-w-[1360px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32 lg:px-16">
          <div className="md:col-span-5">
            <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-black md:text-4xl">Keep the application focused.</h2>
            <p className="font-body mt-6 text-lg font-normal leading-relaxed text-black/80">We are less interested in long introductions than in understanding:</p>
            <ul className="mt-6 space-y-3">
              {BEFORE.map((b) => (
                <li key={b} className="font-body flex items-start gap-3 text-lg font-normal text-black"><span className="mt-2.5 h-2.5 w-2.5 shrink-0 bg-[#5f8f10]" />{b}</li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-7">
            <div className="border border-[#161A15]/20 p-7 md:p-10">
              {status === "done" ? (
                <div data-testid="career-confirm" className="py-6 text-center">
                  <div className="mx-auto inline-flex h-14 w-14 items-center justify-center bg-[#5f8f10] text-2xl font-bold text-black">✓</div>
                  <h3 className="font-display mt-6 text-2xl uppercase tracking-tight text-black">Your application has been received.</h3>
                  <p className="font-body mx-auto mt-4 max-w-md text-lg font-normal leading-relaxed text-black/80">Thank you for your interest in SWITCH. We will review the information and CV you submitted. Only shortlisted candidates will be contacted.</p>
                  <a href="/" className="font-body mt-8 inline-flex items-center gap-2 border border-[#161A15]/20 bg-[#161A15] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white">Return to SWITCH</a>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <label className="font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black">Name</label>
                    <input data-testid="career-name" className={field} value={form.name} onChange={set("name")} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black">Email</label>
                    <input type="email" data-testid="career-email" className={field} value={form.email} onChange={set("email")} placeholder="Your email address" />
                  </div>
                  <div>
                    <label className="font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black">Area of expertise</label>
                    <textarea rows={3} data-testid="career-expertise" className={`${field} resize-none`} value={form.expertise} onChange={set("expertise")} placeholder="Tell us what you do and the kind of problems you are best equipped to solve." />
                  </div>
                  <div>
                    <label className="font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black">CV</label>
                    <input ref={fileRef} type="file" data-testid="career-cv" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" />
                    <button type="button" onClick={() => fileRef.current?.click()} className="font-body flex w-full items-center justify-between border-2 border-dashed border-black/50 bg-white px-4 py-3.5 text-left text-base font-normal text-black/70 hover:border-black">
                      <span>{file ? file.name : "Upload your CV"}</span>
                      <span className="font-semibold uppercase tracking-[0.1em] text-black">{file ? "Change" : "Browse"}</span>
                    </button>
                    <p className="font-body mt-2 text-sm font-normal text-black/60">Accepted: PDF, DOC, DOCX · Max 5 MB · Required.</p>
                  </div>
                  <div>
                    <label className="font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black">Cover letter <span className="normal-case text-black/50">(optional)</span></label>
                    <textarea rows={4} maxLength={1000} data-testid="career-cover" className={`${field} resize-none`} value={form.cover_letter} onChange={set("cover_letter")} placeholder="Tell us briefly why you would like to work with SWITCH and what you could bring." />
                    <p className="font-body mt-1 text-right text-sm text-black/50">{form.cover_letter.length}/1000</p>
                  </div>
                  {error && <p data-testid="career-error" className="font-body text-base font-semibold text-[#b23b2e]">{error}</p>}
                  <button type="submit" disabled={status === "submitting"} data-testid="career-submit" className="font-body inline-flex w-full items-center justify-center gap-3 border border-[#161A15]/20 bg-[#161A15] px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-150 hover:bg-[#5f8f10] hover:border-[#5f8f10] hover:text-black disabled:opacity-60">
                    {status === "submitting" ? "Submitting…" : "Submit Application"} <span aria-hidden>→</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Pager current="/careers" />
    </div>
  );
}
